import { useCallback, useEffect, useRef, useState } from 'react'
import { APPROACH_MS, CARRY_MS, FOLD_MS, IMPACT_MS, LOCK_MS, duration, wait } from './timing'

/**
 * Where the RSVP card was, and how wide, in viewport coordinates.
 *
 * The width matters because the dog has to stop *beside* the card to barge it,
 * and the card's width depends on the viewport. Measuring it lets the positions
 * be handed to CSS as custom properties rather than guessed.
 */
export interface Origin {
  x: number
  y: number
  halfWidth: number
}

export type DeliveryStage =
  /** Nothing playing; the card is on screen as normal. */
  | 'idle'
  /** The card seals shut - the answers are in and no longer editable. */
  | 'locking'
  /** The dog is running in from the right. */
  | 'approach'
  /** He barges the card; it shakes and knocks him back. */
  | 'impact'
  /** The card shrinks down into a letter. */
  | 'folding'
  /** He steps in, takes the letter and leaves. */
  | 'carrying'
  /** Finished: the card is gone and the confirmation can take its place. */
  | 'gone'

export interface LetterDelivery {
  stage: DeliveryStage
  origin: Origin | null
  /**
   * Plays the whole sequence, resolving only once it has finished. Race it
   * against the save so the animation is never cut short and a slow save never
   * leaves the dog stranded:
   *
   *   await Promise.allSettled([save(), delivery.play(cardEl)])
   */
  play: (from: HTMLElement | null) => Promise<void>
  /** Puts the card back, for when the save failed and the guest should retry. */
  reset: () => void
}

export function useLetterDelivery(): LetterDelivery {
  const [stage, setStage] = useState<DeliveryStage>('idle')
  const [origin, setOrigin] = useState<Origin | null>(null)

  // Stops a sequence that is mid-flight from setting state after unmount, or
  // after reset() has already put the card back.
  const runId = useRef(0)

  useEffect(
    () => () => {
      runId.current += 1
    },
    [],
  )

  const play = useCallback(async (from: HTMLElement | null) => {
    runId.current += 1
    const run = runId.current
    const current = () => runId.current === run

    // Measure the card so the letter appears exactly where it shrank, and the
    // dog knows where its edge is.
    if (from) {
      const rect = from.getBoundingClientRect()
      setOrigin({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        halfWidth: rect.width / 2,
      })
    } else {
      setOrigin(null)
    }

    // Each step waits for its own animation, so the stages cannot overlap and
    // the dog is never mid-stride when the card starts shrinking.
    for (const [next, ms] of [
      ['locking', LOCK_MS],
      ['approach', APPROACH_MS],
      ['impact', IMPACT_MS],
      ['folding', FOLD_MS],
      ['carrying', CARRY_MS],
    ] as const) {
      setStage(next)
      await wait(duration(ms))
      if (!current()) return
    }

    setStage('gone')
  }, [])

  const reset = useCallback(() => {
    runId.current += 1
    setStage('idle')
    setOrigin(null)
  }, [])

  return { stage, origin, play, reset }
}
